'use client'

import * as React from 'react'

import { useTrackEvent } from '@/hooks/useTrackEvent'

const toError = (value: unknown): Error => (value instanceof Error ? value : new Error(String(value)))

/** Catches errors React's own error boundaries never see — thrown outside render (event handlers,
 * timers, fetch callbacks) and unhandled promise rejections — and reports them the same way error.tsx
 * reports render failures, so GA4 can show error impact on conversion funnels. */
export const useGlobalErrorTracking = () => {
  const { trackError } = useTrackEvent()

  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      trackError(toError(event.error ?? event.message), 'window.onerror')
    }

    const handleRejection = (event: PromiseRejectionEvent) => {
      trackError(toError(event.reason), 'unhandledrejection')
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [trackError])
}
